import { TestControl } from '../../models/test-control.interface';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../../services/auth/auth.service';
import { CompletionProvidersApiService } from '../../../services/api/completion-providers-api-service';
import { SupabaseClientService } from '../../../services/client/supabase-client.service';
import { InstructionsApiService } from '../../../services/api/instructions-api.service';
import { Subject, takeUntil } from 'rxjs';

/**
 * Factory function to create API test definitions with proper service injection
 */
export function createAPITestDefinitions(
  http: HttpClient,
  auth: AuthService,
  providersService: CompletionProvidersApiService,
  supabase: SupabaseClientService,
  instructionService: InstructionsApiService,
  destroy$: Subject<void>
): TestControl[] {
  
  return [
    {
      id: 'test-interceptor',
      title: 'Test Interceptor',
      description: 'Test HTTP interceptor with external API call',
      category: 'api-tests',
      type: 'button',
      icon: 'http',
      execute: () => testInterceptor(http, destroy$)
    },
    {
      id: 'test-rls-auth',
      title: 'Test RLS Auth',
      description: 'Test row-level security and authentication',
      category: 'api-tests',
      type: 'button',
      icon: 'security',
      execute: () => testRLSAuth(supabase, providersService)
    },
    {
      id: 'test-providers',
      title: 'Test Providers',
      description: 'Test completion providers API',
      category: 'api-tests',
      type: 'button',
      icon: 'api',
      execute: () => testProviders(providersService, destroy$)
    },
    {
      id: 'test-instructions',
      title: 'Test Instructions',
      description: 'Test instruction assembly service',
      category: 'api-tests',
      type: 'button',
      icon: 'description',
      execute: () => testInstructions(auth, instructionService)
    },
    {
      id: 'test-auth-profile',
      title: 'Test Auth Profile',
      description: 'Test user profile retrieval',
      category: 'api-tests',
      type: 'button',
      icon: 'account_circle',
      execute: () => testAuthProfile(auth)
    }
  ];
}

/**
 * Test HTTP interceptor (migrated from runInterceptor)
 */
async function testInterceptor(
  http: HttpClient,
  destroy$: Subject<void>
): Promise<void> {
  console.log('Testing HTTP interceptor...');
  
  return new Promise((resolve, reject) => {
    http.get('https://jsonplaceholder.typicode.com/posts/1')
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (response) => {
          console.log('Interceptor test response:', response);
          resolve();
        },
        error: (err) => {
          console.error('Interceptor test error:', err);
          reject(err);
        }
      });
  });
}

/**
 * Test RLS auth (migrated from testRLSAuth)
 */
async function testRLSAuth(
  supabase: SupabaseClientService,
  providersService: CompletionProvidersApiService
): Promise<void> {
  console.log('Testing RLS authentication...');
  
  try {
    const providers = await supabase.getClient().from('completion_providers').select('*');
    const available_models = await providersService.getModels().toPromise();
    
    console.log('RLS test - providers:', providers);
    console.log('RLS test - available models:', available_models);
  } catch (error) {
    console.error('RLS test error:', error);
    throw error;
  }
}

/**
 * Test providers API
 */
async function testProviders(
  providersService: CompletionProvidersApiService,
  destroy$: Subject<void>
): Promise<void> {
  console.log('Testing providers API...');
  
  return new Promise((resolve, reject) => {
    providersService.getModels()
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (models) => {
          console.log('Providers test - models:', models);
          resolve();
        },
        error: (error) => {
          console.error('Providers test error:', error);
          reject(error);
        }
      });
  });
}

/**
 * Test instructions assembly (migrated from testSnippets)
 */
async function testInstructions(
  auth: AuthService,
  instructionService: InstructionsApiService
): Promise<void> {
  console.log('Testing instructions assembly...');
  
  try {
    const profile = await auth.getProfile();
    console.log('Instructions test - profile:', profile);
    
    // Using a test instruction ID - replace with actual ID
    const instructions = await instructionService.assembleInstructions("0ba5ddda-7514-4b11-8df9-ccdb0e7c4749");
    console.log('Instructions test - assembled instructions:', instructions);
  } catch (error) {
    console.error('Instructions test error:', error);
    throw error;
  }
}

/**
 * Test auth profile retrieval
 */
async function testAuthProfile(auth: AuthService): Promise<void> {
  console.log('Testing auth profile...');
  
  try {
    const profile = await auth.getProfile();
    console.log('Auth profile test - profile:', profile);
  } catch (error) {
    console.error('Auth profile test error:', error);
    throw error;
  }
}